'use client';

import type { ComponentPropsWithoutRef } from 'react';
import React, { useState } from 'react';
import classNames from 'classnames';
import { Country, Locale } from '@fxtr/i18n';
import { getPostcode } from '$api/postcode';
import type { AppGarage } from '$page-router/api/getGaragesByLatLong';
import PostcodeAutocompleteV2, {
  PostcodeAutocompleteV2Variations,
} from '@/components/molecules/PostcodeAutocompleteV2';
import type { LocationProps } from '@/components/molecules/MapWithGarageMarkers';
import { MapWithGarageMarkers } from '@/components/molecules/MapWithGarageMarkers';
import { GARAGE_MAP_CONTENT_COMPONENT_NAME } from './utils';

import styles from './index.module.scss';

export interface GarageMapProps extends ComponentPropsWithoutRef<'div'> {
  readonly location?: LocationProps;
  readonly garages?: AppGarage[];
  readonly setLatLong: ({ lat, lng }: { lat: number; lng: number }) => void;
  readonly selectedGarageId: string;
  readonly setSelectedGarageId: (garageId: string) => void;
  readonly translations: {
    readonly postCodeText: string;
    readonly hideMapText: string;
    readonly expandMapText: string;
  };
  readonly locale: Locale;
}

export function GarageMap({
  location,
  garages,
  setLatLong,
  selectedGarageId,
  setSelectedGarageId,
  translations: { postCodeText, hideMapText, expandMapText },
  locale,
  children,
  className,
  ...props
}: GarageMapProps): JSX.Element {
  const [currentPosition, setCurrentPosition] = useState(
    {} as google.maps.LatLng | google.maps.LatLngLiteral
  );

  const setNewPosition = (lat: number, lng: number) => {
    setCurrentPosition({ lat, lng });
    setLatLong({ lat, lng });
  };

  const setPostcode = async (postcode: string) => {
    if (locale === Locale.FR_FR) {
      const postcodeLocation = await fetch(`/api/postcode?postcode=${postcode}&country=${Country.FR}`).then(
        (res) => res.json()
      );
      if (postcodeLocation) {
        setNewPosition(postcodeLocation.latitude, postcodeLocation.longitude);
      }
    } else {
      const {
        data: {
          result: { latitude, longitude },
        },
      } = await getPostcode(postcode);

      setNewPosition(latitude, longitude);
    }
  };

  return (
    <div className={classNames(GarageMap.displayName, styles.component, className)} {...props}>
      <div className={styles.MapContainer}>
        <div className={styles.PostcodeAutocompleteContainer}>
          <PostcodeAutocompleteV2
            useAutocomplete={locale === Locale.EN_GB}
            variation={PostcodeAutocompleteV2Variations.withButton}
            onCtaButtonClick={setPostcode}
            translations={{
              textPostcode: postCodeText,
            }}
          />
        </div>
        <MapWithGarageMarkers
          location={location}
          currentPosition={currentPosition}
          garages={garages}
          selectedGarageId={selectedGarageId}
          setSelectedGarageId={setSelectedGarageId}
          translations={{ hideMapText, expandMapText }}
        />
      </div>
    </div>
  );
}
GarageMap.displayName = GARAGE_MAP_CONTENT_COMPONENT_NAME;
